import React, { useMemo } from 'react';
import Seo from '../components/Seo';
import HeroBlock from '../components/blocks/HeroBlock';
import SplitBlock from '../components/blocks/SplitBlock';
import MarqueeBlock from '../components/blocks/MarqueeBlock';
import BenefitsBlock from '../components/blocks/BenefitsBlock';
import GridBlock from '../components/blocks/GridBlock';
import TypeCompareBlock from '../components/blocks/TypeCompareBlock';
import LifestyleBlock from '../components/blocks/LifestyleBlock';
import StepsBlock from '../components/blocks/StepsBlock';
import SamplePromoBlock from '../components/blocks/SamplePromoBlock';
import SampleKitBlock from '../components/blocks/SampleKitBlock';
import QuoteBlock from '../components/blocks/QuoteBlock';
import AboutBlock from '../components/blocks/AboutBlock';
import LeadFormBlock from '../components/blocks/LeadFormBlock';
import FinalCTABlock from '../components/blocks/FinalCTABlock';
import BundlesBlock from '../components/blocks/BundlesBlock';
import { useLang } from '../LangContext';
import { usePrices, categoryFromPrice, getBundles } from '../hooks/usePrices';
import { normalizeBlueprintSections } from '../pageBlueprint';
import { Comparison, Empathy, FAQ, Promise, Reviews, TrustDeep } from './HomePage';

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

function deepMerge(a, b) {
  if (!isPlainObject(a) || !isPlainObject(b)) return b ?? a;
  const out = { ...a };
  for (const k of Object.keys(b)) {
    out[k] = isPlainObject(a[k]) && isPlainObject(b[k]) ? deepMerge(a[k], b[k]) : b[k];
  }
  return out;
}

function withSectionOverrides(base, page, type, lang) {
  const globalOverride = page?.sectionOverrides?.[type]?.[lang] || {};
  return deepMerge(base, globalOverride);
}

function normalizeHero(data) {
  const next = { ...data };
  if (!Array.isArray(next.h1)) {
    next.h1 = [next.title1 || next.title || '', next.title2 || next.titleAfter || ''].filter(Boolean);
  }
  if (next.primaryLabel || next.whatsappLabel) {
    next.ctas = (next.ctas || []).map((cta) => {
      if (cta.kind === 'primary' && next.primaryLabel) return { ...cta, label: next.primaryLabel };
      if (cta.kind === 'whatsapp' && next.whatsappLabel) return { ...cta, label: next.whatsappLabel };
      return cta;
    });
  }
  return next;
}

export default function HomeBlueprintPage({ page }) {
  const { lang, t, c } = useLang();
  const prices = usePrices();
  const bundles = useMemo(() => getBundles(prices, lang), [prices, lang]);
  const sections = normalizeBlueprintSections(page.sections).filter((section) => section.enabled !== false);
  const localized = page.translations?.[lang] || page.translations?.nl || {};
  const seo = page.seo?.[lang] || {};
  const title = localized.title || seo.title || page.path;
  const description = seo.description || localized.excerpt || '';

  const products = useMemo(() => {
    const items = c.products.items.map((p) => ({
      ...p,
      price: categoryFromPrice(prices, p.key, lang) || undefined,
    }));
    return withSectionOverrides({ ...c.products, items }, page, 'products', lang);
  }, [c.products, prices, lang, page]);

  const renderSection = (section) => {
    switch (section.type) {
      case 'hero':
        return <HeroBlock key={section.id} {...normalizeHero(withSectionOverrides(c.hero, page, 'hero', lang))} bundles={bundles?.items} />;
      case 'heroMosquito':
        return <HeroBlock key={section.id} {...normalizeHero(withSectionOverrides(c.hero, page, 'heroMosquito', lang))} bundles={bundles?.items} particleEffect="mosquitoes" />;
      case 'bundles':
        return <BundlesBlock key={section.id} data={bundles} />;
      case 'problem':
        return <SplitBlock key={section.id} id="problem" testId="problem-section" {...withSectionOverrides(c.problem, page, 'problem', lang)} />;
      case 'trustBar':
        return <MarqueeBlock key={section.id} {...withSectionOverrides(c.trustBar, page, 'trustBar', lang)} />;
      case 'benefits':
        return <BenefitsBlock key={section.id} {...withSectionOverrides(c.benefits, page, 'benefits', lang)} tone="sand" />;
      case 'empathy':
        return <Empathy key={section.id} t={t} data={withSectionOverrides(c.empathy, page, 'empathy', lang)} />;
      case 'promise':
        return <Promise key={section.id} t={t} data={withSectionOverrides(c.promise, page, 'promise', lang)} />;
      case 'products':
        return <GridBlock key={section.id} {...products} />;
      case 'typeCompare':
        return <TypeCompareBlock key={section.id} {...withSectionOverrides(c.typeCompare, page, 'typeCompare', lang)} />;
      case 'lifestyle':
        return <LifestyleBlock key={section.id} {...withSectionOverrides(c.lifestyle, page, 'lifestyle', lang)} />;
      case 'werkwijze':
        return <StepsBlock key={section.id} {...withSectionOverrides(c.werkwijze, page, 'werkwijze', lang)} />;
      case 'comparison':
        return <Comparison key={section.id} t={t} data={withSectionOverrides(c.comparison, page, 'comparison', lang)} />;
      case 'sample':
        return <SamplePromoBlock key={section.id} {...withSectionOverrides(c.sample, page, 'sample', lang)} tone="sand" />;
      case 'sampleKit':
        return <SampleKitBlock key={section.id} {...withSectionOverrides(c.sampleKit, page, 'sampleKit', lang)} lang={lang} />;
      case 'bigQuote':
        return <QuoteBlock key={section.id} {...withSectionOverrides(c.bigQuote, page, 'bigQuote', lang)} tone="cream" />;
      case 'reviews':
        return <Reviews key={section.id} t={t} data={withSectionOverrides(c.reviews, page, 'reviews', lang)} />;
      case 'about':
        return <AboutBlock key={section.id} {...withSectionOverrides(c.about, page, 'about', lang)} tone="cream" />;
      case 'trustDeep':
        return <TrustDeep key={section.id} t={t} data={withSectionOverrides(c.trustDeep, page, 'trustDeep', lang)} />;
      case 'leadForm':
        return <LeadFormBlock key={section.id} {...withSectionOverrides(c.leadForm, page, 'leadForm', lang)} lang={lang} />;
      case 'faq':
        return <FAQ key={section.id} t={t} data={withSectionOverrides(c.faq, page, 'faq', lang)} />;
      case 'finalCta':
        return <FinalCTABlock key={section.id} {...withSectionOverrides(c.finalCta, page, 'finalCta', lang)} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Seo
        title={seo.title || title}
        description={description}
        path={page.path}
        image={seo.image || localized.image || undefined}
        lang={lang}
        noindex={seo.noindex === true}
      />
      <main data-testid="home-blueprint-page">
        {sections.map(renderSection)}
      </main>
    </>
  );
}
